import { Field, useFormikContext } from 'formik';
import { useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useCheckoutState } from '../../../../utils/checkoutState';
import useUnzer from '../../../../utils/useUnzer';
import CheckboxField from '../../../formik/CheckboxField';
import './style.scss';

type UnzerCardChangeEvent = {
  type: string;
  success: boolean;
  error?: string;
};

// https://docs.unzer.com/payment-methods/card/accept-card-ui-component/
const UnzerCreditcard = () => {
  const { checkoutState } = useCheckoutState();
  const { isLoading, unzerInstance } = useUnzer('unzer_creditcard');
  const { formatMessage } = useIntl();
  const selectedPaymentOption = checkoutState.orderState.payment?.selected?.[0];

  const buttonDisabledRef = useRef({ number: false, expiry: false, cvc: false, holder: false });
  const { values, setFieldValue } = useFormikContext<{
    paymentMethod: string;
    isValid: boolean;
  }>();

  const handleUnzerCardChange = (e: UnzerCardChangeEvent) => {
    if (e.type === undefined) {
      return;
    }

    buttonDisabledRef.current = { ...buttonDisabledRef.current, [e.type]: !!e.success };
    const isValid = Object.values(buttonDisabledRef.current).every(Boolean);

    setFieldValue('unzerCreditcard', { isValid });
  };

  useEffect(() => {
    if (!unzerInstance) {
      return;
    }
    const unzerElementExists =
      IS_WEB_COMPONENT &&
      !import.meta.env.DEV &&
      (() => {
        const cardElement = document.getElementById('card-element-id-number');
        return cardElement && cardElement.childNodes.length > 0;
      })();

    if (!unzerElementExists) {
      const card = unzerInstance.Card();

      card.create('number', {
        containerId: 'card-element-id-number',
        onlyIframe: false
      });

      card.create('expiry', {
        containerId: 'card-element-id-expiry',
        onlyIframe: false
      });

      card.create('cvc', {
        containerId: 'card-element-id-cvc',
        onlyIframe: false
      });

      card.create('holder', {
        containerId: 'card-element-id-holder',
        onlyIframe: false
      });

      if (!window.unzerCard) {
        window.unzerCard = card;
      }

      setTimeout(() => {
        window.unzerCard?.addEventListener('change', handleUnzerCardChange);
      }, 10);
    }
  }, [unzerInstance]);

  useEffect(
    () => () => {
      delete window.unzerCard;
      setFieldValue('unzerCreditcard', { isValid: false });

      if (IS_WEB_COMPONENT && !import.meta.env.DEV) {
        const unzerCardNumber = document.getElementById('card-element-id-number');
        if (unzerCardNumber) {
          unzerCardNumber.innerHTML = '';
        }
        const unzerCardexpiry = document.getElementById('card-element-id-expiry');
        if (unzerCardexpiry) {
          unzerCardexpiry.innerHTML = '';
        }
        const unzerCardCode = document.getElementById('card-element-id-cvc');
        if (unzerCardCode) {
          unzerCardCode.innerHTML = '';
        }
        const unzerCardHolder = document.getElementById('card-element-id-holder');
        if (unzerCardHolder) {
          unzerCardHolder.innerHTML = '';
        }
      }
    },
    []
  );

  if (selectedPaymentOption?.paymentOptionKey !== 'unzer_creditcard' || isLoading) {
    return null;
  }

  return (
    <>
      {IS_WEB_COMPONENT && !import.meta.env.DEV ?
        <slot name="unzer-creditcard-container" id="unzer-creditcard-container" className="unzerUI form unzer-payment">
          <div className="field">
            <slot id="card-element-id-number" name="card-element-id-number" className="unzerInput" />
          </div>
          <div className="field">
            <slot id="card-element-id-expiry" name="card-element-id-expiry" className="unzerInput" />
          </div>
          <div className="field">
            <slot id="card-element-id-cvc" name="card-element-id-cvc" className="unzerInput"></slot>
          </div>
          <div className="field">
            <slot id="card-element-id-holder" name="card-element-id-holder" />
          </div>
        </slot>
      : <form id="unzer-creditcard-container" className="unzerUI form unzer-payment" noValidate>
          <div className="field">
            <div id="card-element-id-number" className="unzerInput"></div>
          </div>
          <div className="two fields">
            <div className="field ten wide">
              <div id="card-element-id-expiry" className="unzerInput"></div>
            </div>
            <div className="field six wide">
              <div id="card-element-id-cvc" className="unzerInput"></div>
            </div>
          </div>
          <div className="field">
            <div id="card-element-id-holder" />
          </div>
        </form>
      }

      {values.paymentMethod === selectedPaymentOption!.paymentOptionKey &&
        selectedPaymentOption?.configuration?.isRecurring && (
          <Field
            name="storeUnzerPaymentMethod"
            style={{ marginTop: '10px' }}
            component={CheckboxField}
            label={formatMessage({ id: 'payment.paymentMethod.unzerCreditcard.storePaymentMethod' })}
            data-test-id="store-unzer-payment-method"
          />
        )}
    </>
  );
};

export default UnzerCreditcard;
